import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Spin } from 'antd';
import { TScopedProviderOption } from 'types/group';
import { TCredentialingProvider } from 'types/credentialing';
import s from './s.module.less';

interface SelectProvidersStepProps {
    // Provider options fetched by the parent; the step only renders when this is non-empty.
    providers?: TScopedProviderOption[];
    // Whether the parent is still loading the provider options.
    loading?: boolean;
    // Providers already added to credentialing — pre-checked so prior picks stay selected.
    roster?: TCredentialingProvider[];
    // Called with the providers the owner chose to credential; parent advances to setup.
    onContinue: (selected: TScopedProviderOption[]) => void;
}

// Step 1 (team owners only): pick which providers already in the practice on Klarity to credential.
const SelectProvidersStep: React.FC<SelectProvidersStepProps> = ({
    providers = [],
    loading = false,
    roster = [],
    onContinue,
}) => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    // Until the user manually toggles, keep the selection in sync with the roster (which may load
    // after the provider options), so previously-credentialed providers come pre-checked.
    const [userTouched, setUserTouched] = useState(false);

    // Pre-check providers already on the roster until the user makes their own selection.
    useEffect(() => {
        if (userTouched || !providers.length) return;
        const rosterIds = new Set(roster.map((r) => r.providerId));
        setSelectedIds(providers.filter((p) => rosterIds.has(p.providerId)).map((p) => p.providerId));
    }, [providers, roster, userTouched]);

    const allSelected = selectedIds.length === providers.length && providers.length > 0;

    const toggle = (providerId: number, checked: boolean) => {
        setUserTouched(true);
        setSelectedIds((prev) => (checked ? [...prev, providerId] : prev.filter((id) => id !== providerId)));
    };

    const toggleAll = () => {
        setUserTouched(true);
        setSelectedIds(allSelected ? [] : providers.map((p) => p.providerId));
    };

    const selected = useMemo(
        () => providers.filter((p) => selectedIds.includes(p.providerId)),
        [providers, selectedIds],
    );

    return (
        <div className={s.wrap}>
            <div className={s.title}>Select the providers you want to credential</div>
            <div className={s.subtitle}>
                These providers are already in your practice on Klarity. Choose who to credential, and you&apos;ll
                complete their details next.
            </div>

            <div className={s.listHeader}>
                <button type="button" className={s.selectAll} onClick={toggleAll} disabled={!providers.length}>
                    {allSelected ? 'Clear all' : 'Select all'}
                </button>
                <span className={s.count}>{selectedIds.length} selected</span>
            </div>

            <Spin spinning={loading}>
                <div className={s.list}>
                    {providers.map((p) => {
                        const checked = selectedIds.includes(p.providerId);
                        return (
                            <label key={p.providerId} className={s.row}>
                                <Checkbox checked={checked} onChange={(e) => toggle(p.providerId, e.target.checked)} />
                                <span className={s.main}>
                                    <span className={s.name}>
                                        {p.firstName} {p.lastName}
                                    </span>
                                    {p.credential && <span className={s.meta}>{p.credential}</span>}
                                </span>
                            </label>
                        );
                    })}
                    {!loading && !providers.length && (
                        <div className={s.empty}>No providers found in your practice.</div>
                    )}
                </div>
            </Spin>

            <div className={s.actions}>
                <Button type="primary" disabled={selectedIds.length === 0} onClick={() => onContinue(selected)}>
                    Continue
                </Button>
            </div>
        </div>
    );
};

export default SelectProvidersStep;
